import styles from "./add-edit-institution-administrators.module.css";
import React, { useEffect, useState } from "react";
import { Row, Col, Switch } from "antd";
import { Controller, useForm } from "react-hook-form";
import { toast } from "react-toastify";
import CustomDrawer from "../../../../../../common/custom-drawer/custom-drawer";
import CustomButton from "../../../../../../common/custom-button/custom-button";
import CustomTable from "../../../../../../common/custom-table/custom-table";
import {
  addInstitutionAdmins,
  updateInstitutionAdmins,
  getAdminPrivileges,
} from "../../../../../../services/admin";
import CustomSelect from "../../../../../../common/input/custom-select/custom-select";
import { Option } from "antd/es/mentions";
import CustomTextfield from "../../../../../../common/input/custom-textfield/custom-textfield";
import { MailOutlined, PhoneOutlined } from "@ant-design/icons";
import CustomDatePicker from "../../../../../../common/input/custom-datepicker/custom-datepicker";
import dayjs from "dayjs";
import { validateDate } from "../../../../../../utils/format-date/format-date";
import { PRIVILEGE_TYPE } from "../../../../../../constants/common";

const privileges_columns = [
  {
    title: "View",
    key: "can_view",
  },
  {
    title: "Add",
    key: "can_add",
  },
  {
    title: "Edit",
    key: "can_edit",
  },
  {
    title: "Delete",
    key: "can_delete",
  },
];

const AddInstitutionAdministrators = ({
  open,
  institutionId,
  onClose,
  selectedInstitutionAdminData,
  setSelectedInstitutionAdminData,
}) => {
  const [loading, setLoading] = useState(false);
  const [adminTableData, setAdminTableData] = useState([]);
  const [accountTableData, setAccountTableData] = useState([]);
  const [activeType, setActiveType] = useState("admin");
  const [basePrivilegesMap, setBasePrivilegesMap] = useState({});
  const tableData =
    activeType === "admin" ? adminTableData : accountTableData;
  const isAccountViewEnabled = adminTableData?.find((item) => item?.key === "accounting")?.can_view;

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm({
    mode: "onChange",
    defaultValues: {
      id: "",
      institution_id: institutionId,
      designation: "",
      full_name: "",
      gender: "",
      date_of_birth: "",
      full_address: "",
      phone_number: "",
      email_address: "",
    },
  });

  const resetFormFields = () => {
    reset({
      institution_id: institutionId,
      email_address: selectedInstitutionAdminData?.email_address ?? "",
      full_name: selectedInstitutionAdminData?.full_name ?? "",
      phone_number: selectedInstitutionAdminData?.phone_number ?? "",
      gender: selectedInstitutionAdminData?.gender ?? "",
      date_of_birth: validateDate(selectedInstitutionAdminData?.date_of_birth) ?? "",
      full_address: selectedInstitutionAdminData?.full_address ?? "",
      designation: selectedInstitutionAdminData?.designation ?? "",
    });
  };

  const getAdminPrivilegeList = async () => {
    setLoading(true);
    try {
      const [adminPrivileges, accountPrivileges] = await Promise.all([
        loadPrivileges("ADMIN", selectedInstitutionAdminData?.privileges),
        loadPrivileges("ACCOUNT", selectedInstitutionAdminData?.account_privileges),
      ]);
      setAdminTableData(adminPrivileges);
      setAccountTableData(accountPrivileges);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const loadPrivileges = async (type, existingPrivileges) => {
    try {
      const response = await getAdminPrivileges({ type });
      if (!response?.status) return [];

      const privileges = response?.data?.data?.privileges ?? [];
      const basePrivileges = privileges?.map((priv) => ({
        key: priv?.name,
        name: priv?.label,
        can_add: 0,
        can_delete: 0,
        can_edit: 0,
        can_view: 0,
      }));
      setBasePrivilegesMap((prev) => ({ ...prev, [type]: basePrivileges }));

      const mergedMap = new Map();
      basePrivileges?.forEach((p) => mergedMap?.set(p?.key, p));
      existingPrivileges?.forEach((p) => mergedMap?.set(p?.key, p));
      return [...mergedMap?.values()];
    } catch (error) {
      console.error(error);
      return [];
    }
  };

  const handleSwitchChange = (index, type, checked) => {
    if (activeType === "admin") {
      const updatedData = [...adminTableData];
      updatedData[index][type] = checked ? 1 : 0;
      setAdminTableData(updatedData);
    } else {
      const updatedData = [...accountTableData];
      updatedData[index][type] = checked ? 1 : 0;
      setAccountTableData(updatedData);
    }
  };

  const onSubmit = async (values) => {
    try {
      setLoading(true);
      const formData = new FormData();
      const isEditing =
        selectedInstitutionAdminData &&
        Object.keys(selectedInstitutionAdminData).length > 0;
      const updatedAccountTableData = isAccountViewEnabled 
        ? accountTableData 
        : basePrivilegesMap?.ACCOUNT;

      if (isEditing) {
        formData.append("institution_id", values.institution_id);
        formData.append("full_name", values.full_name);
        formData.append("gender", values.gender);
        formData.append("date_of_birth", values.date_of_birth);
        formData.append("full_address", values.full_address);
        formData.append("phone_number", values.phone_number);
        formData.append("email_address", values.email_address);
        formData.append("designation", values.designation);
        formData.append("privileges", JSON.stringify(adminTableData));
        formData.append("account_privileges", JSON.stringify(updatedAccountTableData));
      } else {
        const admin = {
          email_address: values.email_address,
          full_name: values.full_name,
          gender: values.gender,
          date_of_birth: values.date_of_birth,
          full_address: values.full_address,
          phone_number: values.phone_number,
          designation: values.designation,
          privileges: JSON.stringify(adminTableData),
          account_privileges: JSON.stringify(updatedAccountTableData),
        };

        formData.append("institution_id", values.institution_id);
        formData.append("admins", JSON.stringify([admin]));
      }

      const response = isEditing
        ? await updateInstitutionAdmins(
            selectedInstitutionAdminData?.id,
            formData
          )
        : await addInstitutionAdmins(formData);

      if (response?.data?.status) {
        toast.success(
          `Administrator ${isEditing ? "Updated" : "Added"} Successfully!`
        );
        setSelectedInstitutionAdminData({});
        onClose();
      } else {
        toast.error("Something went wrong: Invalid API response");
      }
    } catch (error) {
      console.error("Submit Error:", error);
      toast.error("Error! Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (text) => (
        <span style={{ color: "rgba(102, 112, 133, 1)" }}>{text}</span>
      ),
    },
    ...privileges_columns.map((action) => ({
      title: action.title,
      dataIndex: action.key,
      key: action.key,
      render: (_, __, index) => (
        <Switch
          checked={tableData[index]?.[action.key]}
          onChange={(checked) => handleSwitchChange(index, action.key, checked)}
          style={{
            backgroundColor: tableData[index]?.[action.key]
              ? "rgba(57, 181, 74, 1)"
              : undefined,
          }}
        />
      ),
    })),
  ];

  useEffect(() => {
    if (open) {
      resetFormFields();
      getAdminPrivilegeList();
    }
  }, [open]);

  return (
    <CustomDrawer
      title={`${
        selectedInstitutionAdminData &&
        Object.keys(selectedInstitutionAdminData)?.length > 0
          ? "Update"
          : "Add"
      } Institution Administrators`}
      open={open}
      onClose={onClose}
      footer={
        <div className={styles.footer}>
          <CustomButton
            title={`${
              selectedInstitutionAdminData &&
              Object.keys(selectedInstitutionAdminData)?.length > 0
                ? "Update"
                : "Add"
            } Institution Administrators`}
            onClick={handleSubmit(onSubmit)}
          />
        </div>
      }
      loading={loading}
      className={styles.drawer}
      width={730}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <Row gutter={[16, 16]}>
          <h3 className={styles.heading}>
            <span className={styles.dummy_green_rectangle}></span>
            <span>Personal Information:</span>
          </h3>

          <Col span={12}>
            <Controller
              name="full_name"
              control={control}
              rules={{ required: true }}
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => (
                <CustomTextfield
                  id="full_name"
                  label="Full Name"
                  showRequired
                  labelColor="#667085"
                  placeholder="Enter full name"
                  size="large"
                  value={value}
                  onChange={onChange}
                  status={error ? "error" : ""}
                />
              )}
            />
          </Col>

          <Col span={12}>
            <Controller
              name="gender"
              control={control}
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => (
                <CustomSelect
                  id="gender"
                  placeholder="--Select--"
                  size="large"
                  label="Gender"
                  showOptional
                  value={value}
                  onChange={onChange}
                  style={{ width: "100%" }}
                  status={error ? "error" : ""}
                  labelColor="#667085"
                >
                  <Option value="male">Male</Option>
                  <Option value="female">Female</Option>
                </CustomSelect>
              )}
            />
          </Col>

          <Col span={12}>
            <Controller
              name="date_of_birth"
              control={control}
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => (
                <CustomDatePicker
                  id="date_of_birth"
                  label="Date of Birth"
                  showOptional
                  format={"MM-DD-YYYY"}
                  placeholder="Select Date of Birth"
                  size="large"
                  value={value ? dayjs(value, "MM-DD-YYYY") : null}
                  allowClear={false}
                  defaultValue={value ? dayjs(value, "MM-DD-YYYY") : null}
                  onChange={(e, date) => onChange(date)}
                  status={error ? "error" : ""}
                  labelColor="#667085"
                  className={styles.dateInput}
                  errorMessage={errors?.date_of_birth?.message}
                />
              )}
            />
          </Col>
          <Col span={12}>
            <Controller
              name="designation"
              control={control}
              rules={{ required: true }}
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => (
                <CustomTextfield
                  id="designation"
                  label="Designation"
                  showRequired
                  labelColor="#667085"
                  placeholder="Enter Designation"
                  size="large"
                  value={value}
                  onChange={onChange}
                  status={error ? "error" : ""}
                />
              )}
            />
          </Col>

          <h3 className={styles.heading}>
            <span className={styles.dummy_green_rectangle}></span>
            <span>Contact Information:</span>
          </h3>

          <Col span={24}>
            <Controller
              name="full_address"
              control={control}
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => (
                <CustomTextfield
                  id="full_address"
                  label="Full Address"
                  showOptional
                  labelColor="#667085"
                  placeholder="Enter address"
                  size="large"
                  value={value}
                  onChange={onChange}
                  status={error ? "error" : ""}
                />
              )}
            />
          </Col>

          <Col span={12}>
            <Controller
              name="phone_number"
              control={control}
              rules={{ required: true }}
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => (
                <CustomTextfield
                  id="phone_number"
                  label="Phone Number"
                  showRequired
                  labelColor="#667085"
                  placeholder="Enter phone number"
                  size="large"
                  value={value}
                  onChange={onChange}
                  status={error ? "error" : ""}
                  prefix={<PhoneOutlined style={{ transform: "scaleX(-1)" }} />}
                />
              )}
            />
          </Col>

          <Col span={12}>
            <Controller
              name="email_address"
              control={control}
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => (
                <CustomTextfield
                  id="email_address"
                  label="Email"
                  showOptional
                  labelColor="#667085"
                  placeholder="Enter email"
                  size="large"
                  value={value}
                  onChange={onChange}
                  status={error ? "error" : ""}
                  prefix={<MailOutlined />}
                  type="email"
                />
              )}
            />
          </Col>
          <div className={isAccountViewEnabled ? styles.selectContainer : styles.selectWithNoteContainer}>
            <div>
              <h3 className={styles.heading} style={{ marginBottom: 0 }}>
                <span className={styles.dummy_green_rectangle}></span>
                <span>Manage Role:</span>
              </h3>
              {!isAccountViewEnabled ? (
                <p className={styles.selectInputNote}>
                  Note: To assign role management to accounting system, please enable accounting toggle below.
                </p>
              ) : null}
            </div>
          
            <CustomSelect
              placeholder="--Select--"
              size="large"
              value={activeType}
              onChange={(value) => setActiveType(value)}
              style={{ width: 130 }}
              disabled={!isAccountViewEnabled}
            >
              {PRIVILEGE_TYPE?.map((type) => (
                <Option key={type?.value} value={type?.value}>
                  {type?.label}
                </Option>
              ))}
            </CustomSelect>
          </div>

          <Col span={24}>
            <CustomTable
              columns={columns}
              dataSource={tableData}
              loading={loading}
              showMoreIcon={false}
              showTotalText={false}
              pagination={false}
              rowKey={(record, index) => index}
            />
          </Col>
        </Row>
      </form>
    </CustomDrawer>
  );
};

export default AddInstitutionAdministrators;
